import type { FormRecognitionConfig, SampleTemplateRef } from '@capture/shared';
import { createDefaultRecognitionConfig } from '@capture/shared';
import { api } from '@/lib/api-client';
import { useFormConfigStore, resolveV3, activeForm as resolveActiveForm } from '../state/store';
import { RecognitionTab } from './RecognitionTab';

interface RecognitionTabContainerProps {
  /**
   * The form currently in scope, owned by `FormConfigPage` (URL `?form=` →
   * store `activeFormId`). Recognition config is per-form, so the tab reads
   * (and writes) ONLY this form's config — every form fingerprints itself
   * independently. `null` resolves to the default form via the same fallback
   * the store's `activeForm` uses.
   */
  activeFormId: string | null;
  /** F-044 — the job's classification mode, surfaced as an "off" banner when
   *  recognition has no runtime effect. Threaded from FormConfigPage's jobType. */
  classificationMode?: string;
}

/**
 * Wires the (previously orphaned) RecognitionTab into the Form Config editor.
 *
 * Hints / threshold / multi-form policy flow through the form-config store's
 * single-writer draft autosave (the active form's `recognition` + dirty →
 * updateDraft), the same path every other tab uses — so they can't be clobbered
 * (F-012/F-016). Sample-template FILE uploads need multipart, so they go through
 * the existing /api/forms/:versionId/samples endpoints; the returned ref is then
 * mirrored into the active form's recognition via the store so autosave keeps it.
 */
export function RecognitionTabContainer({
  activeFormId,
  classificationMode,
}: RecognitionTabContainerProps) {
  // Source of truth is `contentV3`; mirror the store's `resolveV3` so the tab
  // reads correctly regardless of seeding path, and `activeForm` so the read
  // path resolves a null/stale activeFormId exactly like the write path does.
  const contentV3 = useFormConfigStore((s) => s.contentV3);
  const legacyContent = useFormConfigStore((s) => s.content);
  const version = useFormConfigStore((s) => s.version);
  const setFormRecognition = useFormConfigStore((s) => s.setFormRecognition);

  const v3 = resolveV3({ contentV3, content: legacyContent });
  if (!v3 || !version) return null;

  const config: FormRecognitionConfig =
    resolveActiveForm(v3, activeFormId).recognition ?? createDefaultRecognitionConfig();
  const versionId = version.id;

  async function handleSampleUpload(file: File): Promise<void> {
    const fd = new FormData();
    fd.append('file', file);
    const ref = await api.upload<SampleTemplateRef>(`/forms/${versionId}/samples`, fd);
    // Read fresh from the store (the ACTIVE form) to avoid a stale-closure
    // overwrite on rapid uploads.
    const cur =
      useFormConfigStore.getState().getActiveForm()?.recognition ??
      createDefaultRecognitionConfig();
    useFormConfigStore
      .getState()
      .setFormRecognition({ ...cur, sampleTemplates: [...cur.sampleTemplates, ref] });
  }

  async function handleSampleDelete(id: string): Promise<void> {
    await api.delete(`/forms/${versionId}/samples/${id}`);
    const cur =
      useFormConfigStore.getState().getActiveForm()?.recognition ??
      createDefaultRecognitionConfig();
    useFormConfigStore.getState().setFormRecognition({
      ...cur,
      sampleTemplates: cur.sampleTemplates.filter((s) => s.id !== id),
    });
  }

  return (
    <RecognitionTab
      config={config}
      onChange={setFormRecognition}
      onSampleUpload={(f) => void handleSampleUpload(f)}
      onSampleDelete={(id) => void handleSampleDelete(id)}
      classificationMode={classificationMode}
    />
  );
}
